'use client'

import { VerificationCodeAction } from '@prisma/client'
import { useState } from 'react'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/_components/ui/card'
import { ActionErrorName } from '@/_types/actions'

import { submitEmail } from '../_actions/submit-email'
import { submitPassword } from '../_actions/submit-password'
import { validateVerificationCode } from '../_actions/validate-verification-code'
import CodeStep, { CodeStepSubmitHandler } from './code-step'
import EmailStep, { EmailStepSubmitHandler } from './email-step'
import PasswordStep, { PasswordStepSubmitHandler } from './password-step'
import Success from './success'

enum Step {
  Email,
  Code,
  Password,
  Success,
}

type ForgotPasswordFormProps = {
  defaultEmailValue?: string
}

const ForgotPasswordForm = ({ defaultEmailValue }: ForgotPasswordFormProps) => {
  const [email, setEmail] = useState<string | undefined>()
  const [step, setStep] = useState<Step>(Step.Email)

  const handleEmailStepSubmit: EmailStepSubmitHandler = async (
    data,
    setError
  ) => {
    const response = await submitEmail(data)

    if (
      response.error &&
      response.error.name !== ActionErrorName.UserNotFoundError
    ) {
      setError('email', {
        type: 'manual',
        message: 'Erro ao enviar código de acesso. Tente novamente.',
      })
      return
    }

    setEmail(data.email)
    setStep(Step.Code)
  }

  const handleCodeStepSubmit: CodeStepSubmitHandler = async (
    data,
    setError
  ) => {
    if (!email) {
      return
    }
    const response = await validateVerificationCode({
      code: data.code,
      action: VerificationCodeAction.RESET_PASSWORD,
      email: email,
    })
    if (response.error) {
      return setError('code', {
        type: 'manual',
        message: 'Algo deu errado. Tente novamente mais tarde.',
      })
    }
    if (!response.data?.isValid) {
      return setError('code', {
        type: 'manual',
        message: 'Código inválido.',
      })
    }
    setStep(Step.Password)
  }

  const handlePasswordStepSubmit: PasswordStepSubmitHandler = async (
    data,
    setError
  ) => {
    if (!email) {
      return
    }
    if (data.password !== data.passwordConfirmation) {
      return setError('passwordConfirmation', {
        type: 'manual',
        message: 'As senhas não conferem.',
      })
    }
    const response = await submitPassword({
      email: email!,
      password: data.password,
    })
    if (response.error) {
      return setError('password', {
        type: 'manual',
        message: 'Erro ao cadastrar nova senha. Tente novamente.',
      })
    }
    setStep(Step.Success)
  }

  const renderStep = (step: Step) => {
    return {
      [Step.Email]: (
        <EmailStep
          onSubmit={handleEmailStepSubmit}
          defaultEmailValue={defaultEmailValue}
        />
      ),
      [Step.Code]: <CodeStep onSubmit={handleCodeStepSubmit} />,
      [Step.Password]: <PasswordStep onSubmit={handlePasswordStepSubmit} />,
      [Step.Success]: <Success />,
    }[step]
  }

  const renderDescription = (step: Step) => {
    return {
      [Step.Email]: 'Digite seu e-mail cadastrado abaixo.',
      [Step.Code]: `Enviamos um código de acesso ao email ${email}. Insira o código abaixo para criar uma nova senha.`,
      [Step.Password]: 'Cadastre sua nova senha.',
      [Step.Success]: 'Sua nova senha foi cadastrada com sucesso!',
    }[step]
  }

  return (
    <Card className="min-w-[389px] max-w-[389px] space-y-6 px-8 py-6">
      <CardHeader className="space-y-2 p-0 text-center">
        <CardTitle>
          {step === Step.Success ? 'Sucesso!' : 'Recupere sua senha'}
        </CardTitle>
        <CardDescription>{renderDescription(step)}</CardDescription>
      </CardHeader>
      <CardContent className="p-0">{renderStep(step)}</CardContent>
    </Card>
  )
}

export default ForgotPasswordForm
